import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  keyframes
} from '@mui/material';

const GlobalCopyProgressModal = ({ 
  open, 
  videoTitle = '',
  onComplete = () => {}
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  
  // 애니메이션 키프레임 정의 (기존과 동일)
  const pulseAnimation = keyframes`
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  `;

  const glowAnimation = keyframes`
    0% {
      box-shadow: 0 0 5px rgba(25, 118, 210, 0.3);
    }
    50% {
      box-shadow: 0 0 20px rgba(25, 118, 210, 0.6), 0 0 30px rgba(25, 118, 210, 0.4);
    }
    100% {
      box-shadow: 0 0 5px rgba(25, 118, 210, 0.3);
    }
  `;

  const shimmerAnimation = keyframes`
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  `;

  const bounceAnimation = keyframes`
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-3px);
    }
    60% {
      transform: translateY(-2px);
    }
  `;

  // 진행 단계별 메시지 (기존 분석과 동일)
  const steps = [
    { message: t('analyze.progressTranscript'), threshold: 10 },
    { message: t('analyze.progressSummary'), threshold: 40 },
    { message: t('analyze.progressFinalSummary'), threshold: 50 },
    { message: t('analyze.progressChapters'), threshold: 90 },
    { message: t('analyze.progressCompleted'), threshold: 100 }
  ];

  // 단계별 진행률 업데이트
  useEffect(() => {
    if (!open) {
      setProgress(0);
      setCurrentStep(0);
      return;
    }

    const progressSteps = [0, 10, 40, 70, 100];
    let stepIndex = 0;
    
    // 첫 번째 단계 (0%) 즉시 설정
    setProgress(progressSteps[0]);
    const newStep = steps.findIndex(step => progressSteps[0] <= step.threshold);
    setCurrentStep(newStep === -1 ? steps.length - 1 : newStep);
    stepIndex = 1;
    
    const timer = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        const currentProgress = progressSteps[stepIndex];
        setProgress(currentProgress);
        
        // 현재 단계 업데이트
        const newStep = steps.findIndex(step => currentProgress <= step.threshold);
        setCurrentStep(newStep === -1 ? steps.length - 1 : newStep);
        
        // 100% 도달 시 타이머만 정리하고 상태 유지
        if (currentProgress === 100) {
          clearInterval(timer);
          // 자동 완료 로직 제거 - 외부에서 제어하도록 변경
          return;
        }
        
        stepIndex++;
      }
    }, 200); // 0.2초 간격

    return () => clearInterval(timer);
  }, [open]); // onComplete 의존성 제거

  const getCurrentMessage = () => {
    return steps[currentStep]?.message || t('analyze.analysisProgress');
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          zIndex: 9999,
          position: 'relative'
        }
      }}
      sx={{
        zIndex: 9999,
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9998
        }
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        {/* 진행률 원형 표시 */}
        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
          {/* 배경 원 */}
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              left: -10,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, rgba(25, 118, 210, 0.1), rgba(66, 165, 245, 0.1))',
              animation: `${glowAnimation} 2s ease-in-out infinite`,
            }}
          />
          
          <CircularProgress
            variant="determinate"
            value={progress}
            size={80}
            thickness={4}
            sx={{
              color: '#1976d2',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
                filter: 'drop-shadow(0 0 6px rgba(25, 118, 210, 0.4))',
              }
            }}
          />
          
          {/* 중앙 텍스트 */}
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            <Typography 
              variant="h6" 
              component="div" 
              color="text.secondary"
              sx={{
                fontWeight: 'bold',
                animation: progress < 100 ? `${bounceAnimation} 1s ease-in-out infinite` : 'none'
              }}
            >
              {Math.round(progress)}%
            </Typography>
          </Box>
        </Box>

        {/* 현재 단계 표시 */}
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 1, 
            fontWeight: 600,
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: `${shimmerAnimation} 3s ease-in-out infinite`,
            backgroundSize: '200% 100%'
          }}
        >
          {getCurrentMessage()}
        </Typography>

        {/* 영상 제목 표시 */}
        {videoTitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            "{videoTitle}"
          </Typography>
        )}

        {/* 진행률 바 */}
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: '#f0f0f0',
            overflow: 'hidden',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
              backgroundSize: '200% 100%',
              animation: `${shimmerAnimation} 2s linear infinite`,
              filter: 'drop-shadow(0 0 4px rgba(25, 118, 210, 0.3))'
            }
          }} 
        />

        {/* 단계별 진행 상황 */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {[
            { step: 0, label: t('analyze.stepTranscript'), threshold: 30, icon: '📄' },
            { step: 1, label: t('analyze.stepSummary'), threshold: 60, icon: '📋' },
            { step: 2, label: t('analyze.stepFinalSummary'), threshold: 70, icon: '🎯' },
            { step: 3, label: t('analyze.stepChapters'), threshold: 80, icon: '📑' },
            { step: 4, label: t('analyze.stepCompletion'), threshold: 100, icon: '✅' }
          ].map((stepData, index) => {
            const isCompleted = progress >= stepData.threshold;
            const isCurrent = progress >= (index > 0 ? [30, 60, 70, 80][index-1] : 0) && progress < stepData.threshold;
            
            return (
              <Box key={stepData.step} sx={{ textAlign: 'center', flex: 1 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? '#1976d2' : isCurrent ? '#42a5f5' : '#e0e0e0',
                    color: isCompleted || isCurrent ? 'white' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    margin: '0 auto 4px auto',
                    transition: 'all 0.3s ease',
                    animation: isCurrent ? `${pulseAnimation} 1s ease-in-out infinite` : 'none',
                    boxShadow: isCompleted ? '0 0 8px rgba(25, 118, 210, 0.4)' : 'none'
                  }}
                >
                  <span style={{ fontSize: '8px' }}>{stepData.icon}</span>
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: isCompleted ? '#1976d2' : isCurrent ? '#42a5f5' : '#666',
                    fontWeight: isCurrent ? 600 : 400,
                    fontSize: '8px',
                    animation: isCurrent ? `${bounceAnimation} 2s ease-in-out infinite` : 'none'
                  }}
                >
                  {stepData.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalCopyProgressModal; 